import { useMemo, useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import { NavLink, useNavigate } from 'react-router-dom';
import format from 'date-fns/format';

import FormikTextField from 'components/InputsField/formikInputs/FormikTextField';
import PhoneField from 'components/InputsField/formikInputs/PhoneField';
import { ProfileService } from 'service/ProfileService';
import EditLogoModal from '../../../PopupForms/EditLogo';
import FormikDateInput from 'components/InputsField/formikInputs/FormikDateField';
import CompanyTypeRequest from 'components/PopupForms/CompanyTypeRequest';
import { validation } from './validate';
import { updateCompanyInfo, updateLogo } from 'store/company';
import { FORMAT_DATE } from 'constants/time';

import { PROFILE } from 'helpers/routes';
import DiscardChangesDialog from 'components/PopupForms/DiscardChangesDialog';
import { useNavigationBlocker } from 'hooks/useNavigationBlocker';

import styles from './styles.module.scss';

// Inner component that has access to Formik context for dirty state
const EditProfileForm = ({ dirty, setFieldTouched, companyData, companyLogo, editLogo, t, isSavingRef }) => {
  const [blockerState, setBlockerState] = useState(null);
  const [isTypeRequestOpen, setIsTypeRequestOpen] = useState(false);

  const handleBlock = useCallback(({ proceed, cancel }) => {
    // Check if we're in saving mode - if so, don't block
    if (isSavingRef.current) {
      proceed();
      return;
    }
    setBlockerState({ proceed, cancel });
  }, [isSavingRef]);

  // Only block if dirty
  useNavigationBlocker(dirty, handleBlock);

  const handleConfirm = () => {
    blockerState?.proceed();
    setBlockerState(null);
  };

  const handleCancel = () => {
    blockerState?.cancel();
    setBlockerState(null);
  };

  // Сервер отдаёт название типа строкой, но у компании без роли остаётся пустой массив
  const companyType = typeof companyData.company_roles === 'string' ? companyData.company_roles : '';

  return (
    <>
      {blockerState && (
        <DiscardChangesDialog
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
      {isTypeRequestOpen && (
        <CompanyTypeRequest
          companyId={companyData.id}
          currentType={companyType}
          onClose={() => setIsTypeRequestOpen(false)}
        />
      )}
      <div className={styles.editFormContainer}>
        {/* Desktop Header */}
        <div className={styles.editFormHeader}>
          <span className={styles.editFormHeaderTitle}>{t('profile.editing')}</span>
          <div className={styles.editFormButtons}>
            <button className={styles.saveButton} type="submit">
              {t('profile.save')}
            </button>
            <NavLink className={styles.cancelButton} to={PROFILE.profile}>
              {t('profile.cancel')}
            </NavLink>
          </div>
        </div>

        <div className={styles.editFormContent}>
          {/* Header Section with Logo and Company Fields */}
          <div className={styles.editHeader}>
            <div className={styles.editLogoContainer}>
              <EditLogoModal mode={companyLogo} editLogo={editLogo} />
            </div>

            {/* Mobile Buttons */}
            <div className={styles.mobileButtons}>
              <button className={styles.saveButton} type="submit">
                {t('profile.save')}
              </button>
              <NavLink className={styles.cancelButton} to={PROFILE.profile}>
                {t('profile.cancel')}
              </NavLink>
            </div>

            <div className={styles.editCompanyFields}>
              <FormikTextField
                name="name"
                minLength="3"
                maxLength="64"
                label={t('profile.edit_profile_section.name_field')}
                wrapperClassName={styles.companyNameField}
                autoFocus
                required
              />
              {/* Тип компании задаётся при регистрации: от него зависит набор документов,
                  поэтому меняет его администратор по заявке, а не сам владелец */}
              <div className={styles.companyTypeField}>
                <p className={styles.readOnlyLabel}>{t('profile.company_type.label')}</p>
                <div className={styles.companyTypeRow}>
                  <p className={styles.readOnlyValue}>{companyType || '-'}</p>
                  <button
                    className={styles.companyTypeButton}
                    type="button"
                    onClick={() => setIsTypeRequestOpen(true)}
                  >
                    {t('profile.company_type.change')}
                  </button>
                </div>
                <p className={styles.companyTypeHint}>{t('profile.company_type.hint')}</p>
              </div>
            </div>
          </div>

          {/* Form Sections */}
          <div className={styles.editInfoSections}>
            {/* Column 1: Address & Legal Info */}
            <div className={styles.editInfoSection}>
              <FormikTextField
                name="legal_address"
                minLength="3"
                maxLength="255"
                label={t('profile.legal_address')}
                wrapperClassName={styles.field}
              />
              <FormikTextField
                name="post_address"
                minLength="3"
                maxLength="64"
                label={t('profile.post_address')}
                wrapperClassName={styles.field}
              />
              <FormikTextField
                name="legal_form"
                minLength="2"
                maxLength="64"
                label={t('profile.profile_section.more_subtopics.entity')}
                wrapperClassName={styles.field}
              />
              <div className={styles.readOnlyField}>
                <p className={styles.readOnlyLabel}>{t('profile.profile_section.more_subtopics.employee_number')}</p>
                <p className={styles.readOnlyValue}>{companyData.employees_count || '-'}</p>
              </div>
            </div>

            {/* Column 2: Contact Info */}
            <div className={styles.editInfoSection}>
              <PhoneField
                wrapperClass={styles.field}
                specialLabel=""
                onTouch={setFieldTouched}
                label={t('profile.edit_profile_section.phone')}
                name="phone"
              />
              <div className={styles.readOnlyField}>
                <p className={styles.readOnlyLabel}>{t('profile.profile_section.email')}</p>
                <p className={styles.readOnlyValue}>{companyData.email || '-'}</p>
              </div>
              <FormikTextField
                name="site"
                minLength="3"
                maxLength="64"
                label={t('profile.site')}
                wrapperClassName={styles.field}
              />
            </div>

            {/* Column 3: Registration Info */}
            <div className={styles.editInfoSection}>
              <FormikDateInput
                name="creation_date"
                label={t('profile.profile_section.founding')}
                labelClasses={styles.field}
              />
              <div className={styles.readOnlyField}>
                <p className={styles.readOnlyLabel}>{t('profile.profile_section.tin')}</p>
                <p className={styles.readOnlyValue}>{companyData.payer_number || '-'}</p>
              </div>
              <div className={styles.readOnlyField}>
                <p className={styles.readOnlyLabel}>{t('profile.profile_section.more_subtopics.registration_date')}</p>
                <p className={styles.readOnlyValue}>
                  {companyData.created_at ? format(new Date(companyData.created_at), FORMAT_DATE) : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const EditProfile = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const companyData = useSelector((state) => state.company.companyData);
  const companyLogo = useSelector((state) => state.company.logo);

  const isSavingRef = useRef(false);

  const editLogo = (data, closeCallback) => {
    return ProfileService.editCompanyLogo(companyData.id, data).then(async (res) => {
      await dispatch(updateLogo(res.data.url));
      closeCallback();
    });
  };

  const handleSubmit = async (params, { setErrors }) => {
    const result = await dispatch(
      updateCompanyInfo({
        id: companyData.id,
        params,
      }),
    );
    if (result.payload.errors && result.payload.errors.length > 0) {
      const errors = {};
      result.payload.errors.forEach((error) => {
        errors[error.path[0]] = t(error.type);
      });
      setErrors(errors);
    } else {
      isSavingRef.current = true;
      navigate('/profile/company');
    }
  };

  const initialValues = useMemo(() => {
    const result = { ...companyData };
    if (Object.keys(companyData).length > 0) {
      for (const key in result) {
        if (result[key] === null) {
          result[key] = '';
        }
      }
    }

    return result;
  }, [companyData]);

  return (
    <Formik initialValues={initialValues} validationSchema={validation(t)} onSubmit={handleSubmit} enableReinitialize>
      {({ setFieldTouched, dirty }) => (
        <Form>
          <EditProfileForm
            dirty={dirty}
            setFieldTouched={setFieldTouched}
            companyData={companyData}
            companyLogo={companyLogo}
            editLogo={editLogo}
            t={t}
            isSavingRef={isSavingRef}
          />
        </Form>
      )}
    </Formik>
  );
};

export default EditProfile;
